import type { MarketplaceEntry } from "../types/index";
import { OfficialCheck } from "./OfficialBadge";
import NewBadge from "./NewBadge";
import SharedUsageNote from "./SharedUsageNote";
import styles from "./PluginListView.module.css";

const EXTENSION_TYPE_CLASS: Record<string, string> = {
  plugin: "typeBadgePlugin",
  skill: "typeBadgeSkill",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

interface PluginListViewProps {
  plugins: MarketplaceEntry[];
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
}

export default function PluginListView({ plugins, onSelectPlugin }: PluginListViewProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {/* Column order follows the prototype: Name · Users (30d) · Type · Last Updated ·
              Description. The stale plan doc placed the users column after Last Updated. */}
          <th>Name</th>
          <th>Users (30d)</th>
          <th>Type</th>
          <th>Last Updated</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {plugins.map((plugin) => (
          // The row carries a mouse-only onClick for full-row clickability. The
          // keyboard/SR path goes through the native <button> below, whose click
          // (real or Enter/Space-synthesized) bubbles up to this same handler —
          // so onSelectPlugin fires exactly once and the row stays a plain table
          // row (no role, tabindex, or key handler of its own).
          <tr
            key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
            className={styles.row}
            onClick={() => onSelectPlugin(plugin)}
          >
            <td className={styles.nameCell}>
              <div className={styles.pluginName}>
                <OfficialCheck plugin={plugin} size={15} />
                {/*
                  A real <button> is the actionable control (native keyboard
                  Enter/Space + focus for free). It has no onClick of its own —
                  activation bubbles to the row's handler — so mouse and keyboard
                  share one navigation path with no pointer-intercepting overlay
                  covering the row's text and badges.
                */}
                <button type="button" className={styles.nameButton}>
                  {plugin.pluginName}
                </button>
              </div>
              <div className={styles.repoName}>{plugin.orgOrOwner}/{plugin.repoName}</div>
            </td>
            {/* "no usage yet" rather than the plan doc's "—": #266 AC 2 requires "a clear
                'no usage yet' state rather than blank or broken", and a bare dash here would
                also collide with the Last Updated column's own "—" — same glyph, two
                different meanings, one row. Truthy check keeps a 0 out, as on the card. */}
            <td className={styles.usersCell}>
              {plugin.adoption?.activeUsers30d
                ? <>{plugin.adoption.activeUsers30d}<SharedUsageNote sharedNameUsage={plugin.adoption.sharedNameUsage} /></>
                : "no usage yet"}
            </td>
            {/* The Type cell carries New alongside the type badge — #620 asks for the
                badge on cards, list view and detail, and this is list view's type slot.
                No new COLUMN: the prototype has five, and a sixth for a transient state
                would be a wide, mostly-empty column. */}
            <td className={styles.typeCell}>
              {plugin.extensionType && (
                <span className={`${styles.typeBadge} ${styles[EXTENSION_TYPE_CLASS[plugin.extensionType] ?? "typeBadgePlugin"]}`}>
                  {plugin.extensionType}
                </span>
              )}
              <NewBadge plugin={plugin} />
            </td>
            <td className={styles.updatedCell}>
              {(plugin.lastUpdatedAt && formatDate(plugin.lastUpdatedAt)) || "—"}
            </td>
            <td className={styles.descriptionCell}>
              {plugin.description || "No description available"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
